/**
 * Cart Sync Module
 * Synchronizes the native Shopify AJAX cart with the Storefront API cart
 * This ensures customers remain authenticated when proceeding to checkout
 */

class CartSync {
  constructor() {
    this.initialized = false;
    this.storefrontCart = null;
    this.api = null;
    this.utils = null;
    this.cartSyncReady = false; // Track if cart sync has completed
    this.cartNeedsSyncBeforeCheckout = false; // Track if cart has been modified and needs full sync
    this.CHECKOUT_TIMEOUT = 5000; // 5 second timeout for checkout URL retrieval
  }

  /**
   * Initialize the cart sync module
   * Should be called after ShopifyStorefrontAPI, ShopifyCart, and ShopifyUtils are available
   */
  init() {
    if (this.initialized) return;

    // Wait for dependencies
    if (!window.ShopifyStorefrontAPI || !window.ShopifyCart || !window.ShopifyUtils) {
      console.warn('CartSync: Waiting for dependencies...');
      setTimeout(() => this.init(), 100);
      return;
    }

    this.utils = window.ShopifyUtils;

    // Check if we have config from window (try both uppercase and lowercase)
    const config = window.ShopifyStorefrontConfig || window.shopifyStorefrontConfig;
    if (!config) {
      console.warn('CartSync: No storefront config found. Cart sync disabled.');
      return;
    }

    // Initialize API client
    this.api = new window.ShopifyStorefrontAPI(config);
    this.storefrontCart = new window.ShopifyCart(this.api);

    // Intercept cart additions
    this.interceptCartAdditions();

    // Intercept checkout navigation
    this.interceptCheckoutNavigation();

    this.initialized = true;

    // If customer is logged in via headless auth
    if (this.utils.isCustomerLoggedIn()) {
      // Sync cart on page load
      this.syncCartOnLoad();

      // Hide accelerated checkout buttons (they bypass our auth flow)
      this.hideAcceleratedCheckoutButtons();
    }
  }

  /**
   * Hide accelerated checkout buttons for headless auth users
   * Dynamic checkout buttons (Shop Pay, Apple Pay, etc.) bypass the cart
   * and return null buyerIdentity, which breaks authenticated checkout
   */
  hideAcceleratedCheckoutButtons() {
    // Wait for DOM to be ready and buttons to be rendered
    const hideButtons = () => {
      const selectors = [
        'shopify-accelerated-checkout-cart',
        '.additional-checkout-buttons',
        '.shopify-payment-button',
        '[data-shopify="dynamic-checkout-cart"]',
        '.dynamic-checkout__buttons'
      ];

      let hiddenCount = 0;
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          if (el.style.display !== 'none') {
            el.style.display = 'none';
            hiddenCount++;
          }
        });
      });
    };

    // Run immediately
    hideButtons();

    // Also run after a short delay (buttons might be dynamically added)
    setTimeout(hideButtons, 500);
    setTimeout(hideButtons, 1500);

    // Observe DOM for dynamically added buttons
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length > 0) {
          shouldCheck = true;
        }
      });
      if (shouldCheck) {
        hideButtons();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Disconnect observer after 10 seconds to prevent memory leaks
    setTimeout(() => observer.disconnect(), 10000);
  }

  /**
   * Intercept fetch requests to cart endpoints
   * Handles additions, changes, updates, and clears
   */
  interceptCartAdditions() {
    const self = this;
    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);

      const url = args[0];
      const options = args[1] || {};

      if (typeof url === 'string') {
        // Check if this is a cart change/update/clear request (item removal or quantity change)
        // IMPORTANT: Check this FIRST before cart additions, because the order matters
        // These operations require clearing the Storefront cart immediately
        if ((url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear'))
          && options.method === 'POST') {
          // Mark cart as needing full sync before checkout
          self.cartNeedsSyncBeforeCheckout = true;

          // CRITICAL FIX: Clear the Storefront cart immediately so that subsequent
          // cart additions will create a fresh cart instead of adding to stale cart
          if (self.storefrontCart) {
            self.storefrontCart.clearStoredCart();
          }
        }

        // Check if this is a cart add request
        if (url.includes('/cart/add') && options.method === 'POST') {
          // Clone the response so we can read it
          const clonedResponse = response.clone();

          try {
            const data = await clonedResponse.json();

            // Only sync if add was successful (no error status)
            if (!data.status && data.id) {
              // If cart needs sync (was modified/cleared), skip incremental add
              // The full sync at checkout will handle it
              if (self.cartNeedsSyncBeforeCheckout) {
                // Full sync at checkout will handle it
              } else {
                // Sync to Storefront API cart in background
                self.syncCartAddition(data).catch(err => {
                  console.warn('CartSync: Failed to sync cart addition', err);
                  // If sync fails, mark for full sync before checkout
                  self.cartNeedsSyncBeforeCheckout = true;
                });
              }
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      }

      return response;
    };
  }

  /**
   * Check if the cart sync module is ready for checkout interception
   * @returns {boolean}
   */
  isReadyForCheckout() {
    return this.initialized && this.utils && this.storefrontCart;
  }

  /**
   * Safely handle checkout with fallback to native checkout on any error
   */
  async safeRedirectToCheckout() {
    try {
      await this.redirectToAuthenticatedCheckout();
    } catch (error) {
      console.error('CartSync: Critical error during checkout redirect, using native checkout', error);
      window.location.href = '/checkout';
    }
  }

  /**
   * Intercept clicks on checkout buttons and links
   * Redirect to authenticated checkout URL for logged-in customers
   */
  interceptCheckoutNavigation() {
    const self = this;

    // Intercept form submissions - both direct /checkout forms and cart forms with checkout button
    document.addEventListener('submit', async function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      // Check if this is a checkout submission
      const isCheckoutForm = form.action && form.action.includes('/checkout');
      const hasCheckoutButton = event.submitter && event.submitter.name === 'checkout';

      if (isCheckoutForm || hasCheckoutButton) {
        // Only intercept if module is ready and customer is logged in
        if (self.isReadyForCheckout() && self.utils.isCustomerLoggedIn()) {
          event.preventDefault();
          event.stopPropagation();
          await self.safeRedirectToCheckout();
        }
        // If not ready, allow native form submission to proceed
      }
    }, true);

    // Intercept clicks on checkout links/buttons
    document.addEventListener('click', async function (event) {
      const target = event.target;

      // Check for checkout links and buttons
      const checkoutLink = target.closest('a[href*="/checkout"]');
      const checkoutButton = target.closest('button[name="checkout"], input[name="checkout"], input[type="submit"][value*="Checkout"], button.checkout-button, [data-checkout-button]');

      if (checkoutLink || checkoutButton) {
        // Only intercept if module is ready and customer is logged in
        if (self.isReadyForCheckout() && self.utils.isCustomerLoggedIn()) {
          event.preventDefault();
          event.stopPropagation();
          await self.safeRedirectToCheckout();
        }
        // If not ready, allow native checkout link/button to work
      }
    }, true);

    // Also intercept keyboard events on checkout buttons (Enter key)
    document.addEventListener('keydown', async function (event) {
      if (event.key !== 'Enter') return;

      const target = event.target;
      const checkoutButton = target.closest('button[name="checkout"], input[name="checkout"]');

      if (checkoutButton && self.isReadyForCheckout() && self.utils.isCustomerLoggedIn()) {
        event.preventDefault();
        event.stopPropagation();
        await self.safeRedirectToCheckout();
      }
      // If not ready, allow native Enter key behavior
    }, true);
  }

  /**
   * Sync a cart addition to the Storefront API cart
   * @param {Object} cartItem - The item that was added to native cart
   */
  async syncCartAddition(cartItem) {
    if (!this.storefrontCart || !this.utils.isCustomerLoggedIn()) {
      return;
    }

    try {
      // Convert line item properties to attributes
      const attributes = [];
      if (cartItem.properties) {
        Object.entries(cartItem.properties).forEach(([key, value]) => {
          if (value) {
            attributes.push({ key, value: String(value) });
          }
        });
      }

      // Create line input
      const line = this.storefrontCart.createLineInput(
        cartItem.variant_id || cartItem.id,
        cartItem.quantity || 1,
        attributes
      );

      // Add to Storefront API cart
      const result = await this.storefrontCart.addLines([line]);

      if (result.success) {
        // Item synced to Storefront API cart
      } else {
        console.warn('CartSync: Failed to sync item', result.error);
      }
    } catch (error) {
      console.error('CartSync: Error syncing cart addition', error);
    }
  }

  /**
   * Sync cart on page load for logged-in customers
   */
  async syncCartOnLoad() {
    if (!this.storefrontCart || !this.utils.isCustomerLoggedIn()) {
      this.cartSyncReady = true; // Mark as ready even if not syncing
      return;
    }

    try {
      const customerToken = this.utils.getCustomerToken();
      const cartId = this.storefrontCart.getStoredCartId();

      if (cartId) {
        // Update buyer identity on existing cart
        await this.storefrontCart.updateBuyerIdentity(customerToken);
      } else {
        // Fetch native cart to sync items
        const nativeCartResponse = await fetch('/cart.js');
        const nativeCart = await nativeCartResponse.json();

        if (nativeCart.items && nativeCart.items.length > 0) {
          // Create new cart with items and buyer identity
          const lines = nativeCart.items.map(item => {
            const attributes = [];
            if (item.properties) {
              Object.entries(item.properties).forEach(([key, value]) => {
                if (value) {
                  attributes.push({ key, value: String(value) });
                }
              });
            }
            return this.storefrontCart.createLineInput(item.variant_id, item.quantity, attributes);
          });

          const result = await this.storefrontCart.createCart({
            lines,
            customerAccessToken: customerToken
          });

          if (result.success) {
            // Created new Storefront API cart with existing items
          }
        } else {
          // Create empty cart with buyer identity
          await this.storefrontCart.createCart({
            customerAccessToken: customerToken
          });
        }
      }

      this.cartSyncReady = true;
      this.cartNeedsSyncBeforeCheckout = false;
    } catch (error) {
      console.error('CartSync: Error syncing cart on load', error);
      this.cartSyncReady = true; // Mark as ready even on error to allow fallback checkout
    }
  }

  /**
   * Full sync: Clear Storefront cart and recreate from native Shopify cart
   * This ensures the Storefront cart exactly matches the native cart
   * Call this before checkout if cart has been modified
   */
  async fullSyncCartFromNative() {
    if (!this.storefrontCart || !this.utils.isCustomerLoggedIn()) {
      return { success: false, error: 'Not logged in or cart not initialized' };
    }

    try {
      const customerToken = this.utils.getCustomerToken();

      // Fetch current native cart
      const nativeCartResponse = await fetch('/cart.js');
      const nativeCart = await nativeCartResponse.json();

      // Clear the stored cart ID to force creation of a new cart
      this.storefrontCart.clearStoredCart();

      if (nativeCart.items && nativeCart.items.length > 0) {
        // Create new cart with all items from native cart
        const lines = nativeCart.items.map(item => {
          const attributes = [];
          if (item.properties) {
            Object.entries(item.properties).forEach(([key, value]) => {
              if (value) {
                attributes.push({ key, value: String(value) });
              }
            });
          }
          return this.storefrontCart.createLineInput(item.variant_id, item.quantity, attributes);
        });

        const result = await this.storefrontCart.createCart({
          lines,
          customerAccessToken: customerToken
        });

        if (result.success) {
          this.cartNeedsSyncBeforeCheckout = false;
          return { success: true, cart: result.cart };
        } else {
          console.error('CartSync: Full sync failed', result.error);
          return { success: false, error: result.error };
        }
      } else {
        // Native cart is empty - don't create an empty Storefront cart
        // Just return success with empty flag
        // Native cart is empty
        this.cartNeedsSyncBeforeCheckout = false;
        return { success: true, cart: null, isEmpty: true };
      }
    } catch (error) {
      console.error('CartSync: Error during full sync', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Validate if a checkout URL is valid
   * @param {string} url - The URL to validate
   * @returns {boolean}
   */
  isValidCheckoutUrl(url) {
    if (!url || typeof url !== 'string') {
      console.warn('CartSync: Invalid checkout URL - empty or not a string');
      return false;
    }

    try {
      const parsedUrl = new URL(url);
      // Check if it's a Shopify checkout URL
      // Shopify checkout URLs can be:
      // - https://store.myshopify.com/checkouts/...
      // - https://store.myshopify.com/cart/c/... (Storefront API checkout)
      // - https://checkout.shopify.com/...
      const isShopifyCheckout = parsedUrl.hostname.includes('myshopify.com') ||
        parsedUrl.hostname.includes('shopify.com') ||
        parsedUrl.pathname.includes('/checkouts/') ||
        parsedUrl.pathname.includes('/cart/c/');

      if (!isShopifyCheckout) {
        console.warn('CartSync: Checkout URL does not match Shopify patterns:', url);
      }

      return isShopifyCheckout;
    } catch (e) {
      console.warn('CartSync: Error parsing checkout URL:', e);
      return false;
    }
  }

  /**
   * Wait for cart sync to be ready with timeout
   * @param {number} timeout - Maximum time to wait in ms
   * @returns {Promise<boolean>}
   */
  async waitForCartSync(timeout = 3000) {
    if (this.cartSyncReady) return true;

    const startTime = Date.now();
    return new Promise((resolve) => {
      const checkReady = () => {
        if (this.cartSyncReady) {
          resolve(true);
        } else if (Date.now() - startTime >= timeout) {
          console.warn('CartSync: Cart sync timeout, proceeding with fallback');
          resolve(false);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    });
  }

  /**
   * Redirect to authenticated checkout URL with timeout, retry logic, and fallback
   */
  async redirectToAuthenticatedCheckout() {
    // Show loading indicator immediately
    this.showCheckoutLoading();

    // If storefrontCart is not available, fall back immediately
    if (!this.storefrontCart) {
      console.warn('CartSync: Storefront cart not available, using native checkout');
      window.location.href = '/checkout';
      return;
    }

    try {
      // Wait for initial cart sync to complete (with timeout)
      await this.waitForCartSync(3000);

      // Check if we have a stored cart ID - if not, we MUST do a full sync
      // This handles the case when cache is cleared but native cart still has items
      const hasStoredCartId = !!this.storefrontCart.getStoredCartId();

      // Do a full sync if:
      // 1. Cart has been modified since last sync, OR
      // 2. No stored cart ID exists (cache was cleared, fresh session, etc.)
      if (this.cartNeedsSyncBeforeCheckout || !hasStoredCartId) {
        const reason = !hasStoredCartId ? 'no stored cart ID' : 'cart was modified';
        const syncResult = await this.fullSyncCartFromNative();

        if (!syncResult.success) {
          console.warn('CartSync: Full sync failed, falling back to native checkout');
          window.location.href = '/checkout';
          return;
        }

        // If native cart is empty, use native checkout (it will show empty cart message)
        if (syncResult.isEmpty) {
          console.warn('CartSync: Native cart is empty, using native checkout');
          window.location.href = '/checkout';
          return;
        }
      }

      // Attempt to get checkout URL with retry logic
      let checkoutUrl = await this.getCheckoutUrlWithRetry();

      // Validate the checkout URL before redirecting
      if (checkoutUrl && this.isValidCheckoutUrl(checkoutUrl)) {
        window.location.href = checkoutUrl;
      } else {
        // Invalid or missing checkout URL - fall back to native checkout
        console.warn('CartSync: Invalid or missing checkout URL after retries, falling back to native');
        window.location.href = '/checkout';
      }
    } catch (error) {
      // On any error (including timeout), fall back to native checkout
      console.error('CartSync: Error getting authenticated checkout URL', error);
      window.location.href = '/checkout';
    }
  }

  /**
   * Get checkout URL with retry logic
   * If first attempt fails, creates a fresh cart and tries again
   * @returns {Promise<string|null>}
   */
  async getCheckoutUrlWithRetry() {
    // Create a promise that will timeout
    const createTimeoutPromise = () => new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Checkout URL timeout')), this.CHECKOUT_TIMEOUT);
    });

    // First attempt
    try {
      const checkoutUrl = await Promise.race([
        this.storefrontCart.getAuthenticatedCheckoutUrl(),
        createTimeoutPromise()
      ]);

      if (checkoutUrl && this.isValidCheckoutUrl(checkoutUrl)) {
        return checkoutUrl;
      }
      console.warn('CartSync: First attempt returned invalid URL, will retry');
    } catch (error) {
      console.warn('CartSync: First attempt failed:', error.message);
    }

    // Retry with fresh cart
    try {

      const syncResult = await this.fullSyncCartFromNative();

      if (!syncResult.success || syncResult.isEmpty) {
        console.warn('CartSync: Fresh cart sync failed or cart is empty');
        return null;
      }

      // Try getting checkout URL again with fresh cart
      const checkoutUrl = await Promise.race([
        this.storefrontCart.getAuthenticatedCheckoutUrl(),
        createTimeoutPromise()
      ]);

      if (checkoutUrl && this.isValidCheckoutUrl(checkoutUrl)) {

        return checkoutUrl;
      }
    } catch (retryError) {
      console.error('CartSync: Retry also failed:', retryError.message);
    }

    return null;
  }

  /**
   * Show loading indicator during checkout redirect
   */
  showCheckoutLoading() {
    // Find checkout buttons and disable them
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], .checkout-button'
    );

    checkoutButtons.forEach(button => {
      if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
        button.disabled = true;
      }
      button.classList.add('loading');

      // Add loading text if it's a button
      if (button instanceof HTMLButtonElement) {
        button.dataset.originalText = button.textContent || '';
        button.textContent = 'Loading...';
      }
    });
  }

  /**
   * Handle customer login - sync cart with new customer identity
   */
  async handleCustomerLogin() {
    if (!this.storefrontCart) return;

    try {
      const result = await this.storefrontCart.syncWithCustomerAuth();

    } catch (error) {
      console.error('CartSync: Error syncing cart after login', error);
    }
  }

  /**
   * Handle customer logout - optionally clear cart
   * @param {boolean} clearCart - Whether to clear the cart entirely
   */
  handleCustomerLogout(clearCart = false) {
    if (!this.storefrontCart) return;

    this.storefrontCart.clearStoredCart();
    this.storefrontCart.handleLogout(clearCart);
    setTimeout(async () => {
      await cookieStore.delete("cart")

    }, 1000)

  }

  /**
   * Manually sync cart (can be called after programmatic cart additions)
   * @param {Object} cartItem - Cart item data
   */
  async manualSync(cartItem) {
    if (!this.storefrontCart || !this.utils.isCustomerLoggedIn()) {
      return { success: false, error: 'Not logged in or cart not initialized' };
    }

    return await this.syncCartAddition(cartItem);
  }
}

// Create singleton instance
const cartSync = new CartSync();

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.CartSync = cartSync;

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => cartSync.init());
  } else {
    // DOM is already ready, but wait a bit for other scripts to load
    setTimeout(() => cartSync.init(), 100);
  }
}

